const KEYWORD_PARAM = 'q';

/**
 * Decode the browse keyword URL param (`?q=`).
 * Mirrors `items-search-query-controller.js#checkRouteParams`:
 * `routeParams.q.split("+").join(" ")`.
 */
export function decodeKeywordParam(raw: string | null | undefined): string {
    if (!raw) {
        return '';
    }
    return raw.split('+').join(' ');
}

/**
 * Encode a browse keyword for the URL.
 * Mirrors Angular `urlParam` directive: spaces become `+`.
 */
export function encodeKeywordParam(keyword: string): string {
    if (!keyword) {
        return '';
    }
    return keyword.split(' ').join('+');
}

export function readKeywordFromUrl(search: string = window.location.search): string {
    const match = search.match(/[?&]q=([^&]*)/);
    if (!match) {
        return '';
    }
    return decodeKeywordParam(decodeURIComponent(match[1]));
}

function buildSearchWithKeyword(existingSearch: string, keyword: string): string {
    const params = new URLSearchParams(existingSearch);
    params.delete('q');

    const rest = params.toString();
    const encoded = encodeKeywordParam(keyword);

    if (!encoded) {
        return rest ? `?${rest}` : '';
    }

    const qPart = `q=${encoded}`;
    return rest ? `?${rest}&${qPart}` : `?${qPart}`;
}

/**
 * Write the browse keyword to the URL without a full navigation.
 * Uses Angular `$location` when available so `$routeUpdate` keeps working;
 * falls back to `history.replaceState` (Storybook / unit tests).
 */
export function writeKeywordToUrl(keyword: string): void {
    const encoded = encodeKeywordParam(keyword);

    try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const angularGlobal = (window as any).angular;
        const injector = angularGlobal?.element(document.body)?.injector?.();
        if (injector) {
            const $location = injector.get('$location') as {
                search: (key: string, value?: string | null) => void;
            };
            $location.search(KEYWORD_PARAM, encoded || null);
            return;
        }
    } catch {
        // Fall through to the native URL API below.
    }

    const url = new URL(window.location.href);
    url.search = buildSearchWithKeyword(url.search, keyword);
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
}
